import { useEffect, useState } from 'react';

/**
 * FASE 29 (29.A.3) — hook REATIVO ao viewport "mobile" (<=680px, o mesmo breakpoint da mobile-nav
 * e do `isMobileViewport`). Antes, `isMobileViewport()` so era lido DENTRO de handlers (no corpo do
 * render nunca) — qualquer reorganizacao da UI por viewport ficaria CONGELADA no estado inicial.
 * Este hook resolve isso: le o estado no mount E re-renderiza ao GIRAR/redimensionar (listener de
 * `matchMedia`), para a barra de atalhos (e qualquer consumidor) se reorganizar sem reload.
 *
 * Defensivo: se `matchMedia` nao existir (jsdom/SSR), assume NAO-mobile e nunca quebra.
 */
export const MOBILE_MEDIA_QUERY = '(max-width: 680px)';

function readMatch(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  } catch {
    return false;
  }
}

export function useIsMobile(): boolean {
  const [mobile, setMobile] = useState<boolean>(readMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia(MOBILE_MEDIA_QUERY);
    } catch {
      return;
    }
    const onChange = (): void => setMobile(mql.matches);
    // Sincroniza caso a viewport tenha mudado entre o 1o render e o effect.
    onChange();
    // `addEventListener('change')` moderno; `addListener` legado (Safari antigo) como fallback.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return mobile;
}
